/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, {
  createContext,
  type HTMLAttributes,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
type ThemeContextType = 'light' | 'dark';

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined,
);

type TokensContextType = {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const TokensContext = createContext<TokensContextType | undefined>(
  undefined,
);

type NeedleThemeHook = () => {
  theme: ThemeContextType;
  /**
   * A classname that can be used to scope
   * elements that are portal-ed like
   * Modals, Popovers, etc.
   */
  themeClassName: string;
  tokens?: TokensContextType;
};
/**
 * Hook to access the current theme effortlessly
 */
export const useNeedleTheme: NeedleThemeHook = () => {
  let theme = useContext(ThemeContext);
  /**
   * If the theme is undefined, we are not
   * inside a `NeedleThemeProvider` .
   * Defaults to `light` theme.
   */
  if (theme === undefined) {
    theme = 'light';
  }

  const tokens = useContext(TokensContext);

  return {
    theme,
    themeClassName: `ndl-theme-${theme}`,
    tokens,
  };
};

interface NeedleThemeProviderProps extends PropsWithChildren {
  theme?: 'dark' | 'light';
  tokens?: TokensContextType;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  /**
   * Nested theme providers will receive a wrapping `span` tag
   * in order to set correctly the default text `color` that all nested
   * children will inherit by adding the respective class `ndl-theme-{theme}`.
   *
   * If an extra wrapper is not desired, pass `{ isWrappingChildren : false }`.
   * This requires child components accept and consumer className prop.
   *
   */
  wrapperProps?: HTMLAttributes<HTMLElement> & { isWrappingChildren?: boolean };
}

export const NeedleThemeProvider = ({
  theme = 'light',
  tokens,
  children,
  wrapperProps,
}: NeedleThemeProviderProps) => {
  const clonedChildren = useMemo(() => {
    const { isWrappingChildren, className, ...restWrapperProps } =
      wrapperProps || {};
    const props = {
      ...restWrapperProps,
      className: classNames(className, `ndl-theme-${theme}`),
    };

    if (isWrappingChildren !== true) {
      return React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          ...props,
          className: classNames(child.props.className, props.className),
        });
      });
    } else {
      return (
        <span
          {...props}
          className={classNames('ndl-theme-wrapper', props.className)}
        >
          {children}
        </span>
      );
    }
  }, [wrapperProps, theme, children]);

  return (
    <ThemeContext.Provider value={theme}>
      <TokensContext.Provider value={tokens}>
        {clonedChildren}
      </TokensContext.Provider>
    </ThemeContext.Provider>
  );
};
